import React, { useState, useEffect } from 'react';
import { Typography, Badge } from 'components/atoms';
import { EmployeeReviewData } from 'components/pages/EmployeeReview';
import { EmployeeReviewPagination } from './Pagination';
import { PerformanceTooltip } from './PerformanceTooltip';
import './index.scss';

interface Props {
  data: EmployeeReviewData[];
  onEmployeeClick: (employee: EmployeeReviewData) => void;
  showGoalsColumn?: boolean;
  showScheduleColumn?: boolean;
  showPerformanceColumn?: boolean;
  showStatusColumn?: boolean;
  showAcceptanceStatusColumn?: boolean;
  showNextReviewColumn?: boolean;
  showPagination?: boolean;
  currentPage?: number;
  itemsPerPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
}

export const EmployeeReviewTable = ({
  data,
  onEmployeeClick,
  showGoalsColumn = false,
  showScheduleColumn = false,
  showPerformanceColumn = true,
  showStatusColumn = true,
  showAcceptanceStatusColumn = false,
  showNextReviewColumn = true,
  showPagination = false,
  currentPage = 1,
  itemsPerPage = 10,
  totalItems = 0,
  onPageChange,
  onItemsPerPageChange
}: Props) => {
  const [tooltipState, setTooltipState] = useState<{
    isVisible: boolean;
    performance: any;
    position: { x: number; y: number };
    showBelow: boolean;
    isPinned: boolean;
  }>({
    isVisible: false,
    performance: null,
    position: { x: 0, y: 0 },
    showBelow: false,
    isPinned: false
  });

  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [hideTimeout]);

  const handleStatusHover = (event: React.MouseEvent, performance: any) => {
    if (!performance) return;

    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipHeight = 400;
    const tooltipWidth = 320;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    const showBelow = spaceAbove < tooltipHeight && spaceBelow > spaceAbove;

    let xPosition = rect.left + rect.width / 2;
    const spaceLeft = xPosition - tooltipWidth / 2;
    const spaceRight = window.innerWidth - (xPosition + tooltipWidth / 2);

    if (spaceLeft < 10) {
      xPosition = tooltipWidth / 2 + 10;
    } else if (spaceRight < 10) {
      xPosition = window.innerWidth - tooltipWidth / 2 - 10;
    }

    setTooltipState({
      isVisible: true,
      performance,
      position: {
        x: xPosition,
        y: showBelow ? rect.bottom : rect.top
      },
      showBelow,
      isPinned: false
    });
  };

  const handleStatusClick = (event: React.MouseEvent, performance: any) => {
    event.stopPropagation();

    if (!performance) return;

    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipHeight = 400;
    const tooltipWidth = 320;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;

    const showBelow = spaceAbove < tooltipHeight && spaceBelow > spaceAbove;

    let xPosition = rect.left + rect.width / 2;
    const spaceLeft = xPosition - tooltipWidth / 2;
    const spaceRight = window.innerWidth - (xPosition + tooltipWidth / 2);

    if (spaceLeft < 10) {
      xPosition = tooltipWidth / 2 + 10;
    } else if (spaceRight < 10) {
      xPosition = window.innerWidth - tooltipWidth / 2 - 10;
    }

    if (tooltipState.isVisible && tooltipState.isPinned) {
      setTooltipState((prev) => ({
        ...prev,
        isVisible: false,
        isPinned: false
      }));
    } else {
      setTooltipState({
        isVisible: true,
        performance,
        position: {
          x: xPosition,
          y: showBelow ? rect.bottom : rect.top
        },
        showBelow,
        isPinned: true
      });
    }
  };

  const handleStatusLeave = () => {
    if (tooltipState.isPinned) return;

    const timeout = setTimeout(() => {
      setTooltipState((prev) => ({ ...prev, isVisible: false }));
    }, 300);

    setHideTimeout(timeout);
  };

  const handleTooltipEnter = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
  };

  const handleTooltipLeave = () => {
    if (tooltipState.isPinned) return;

    setTooltipState((prev) => ({ ...prev, isVisible: false }));
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
  };

  const handleBackgroundClick = () => {
    if (tooltipState.isVisible) {
      setTooltipState((prev) => ({
        ...prev,
        isVisible: false,
        isPinned: false
      }));
    }
  };

  const getAcceptanceStatusBadge = (
    acceptanceStatus: 'accepted' | 'declined' | 'pending' | 'mixed',
    goalDetails: {
      goalTitle: string;
      acceptanceStatus: 'Accepted' | 'Declined' | 'Pending';
    }[],
    performance?: any
  ) => {
    const badgeElement = (() => {
      switch (acceptanceStatus) {
        case 'accepted':
          return <Badge label="Accepted" size="sm" type="success" />;
        case 'declined':
          return <Badge label="Declined" size="sm" type="danger" />;
        case 'pending':
        default:
          return <Badge label="Pending" size="sm" type="primary" />;
      }
    })();

    if (performance) {
      return (
        <div
          className="status-badge-wrapper"
          onMouseEnter={(e) => handleStatusHover(e, performance)}
          onMouseLeave={handleStatusLeave}
          onClick={(e) => handleStatusClick(e, performance)}
        >
          {badgeElement}
        </div>
      );
    }

    return badgeElement;
  };

  const getStatusBadge = (status: string, performance?: any) => {
    const badgeElement = (() => {
      switch (status) {
        case 'pending':
          return <Badge label="Pending" size="sm" type="primary" />;
        case 'approved':
          return <Badge label="Approved" size="sm" type="success" />;
        case 'ongoing':
          return <Badge label="Ongoing" size="sm" type="warning" />;
        case 'completed':
          return <Badge label="Completed" size="sm" type="success" />;
        case 'incomplete':
          return <Badge label="Incomplete" size="sm" type="danger" />;
        default:
          return <Badge label="Unknown" size="sm" type="secondary" />;
      }
    })();

    if (performance) {
      return (
        <div
          className="status-badge-wrapper"
          onMouseEnter={(e) => handleStatusHover(e, performance)}
          onMouseLeave={handleStatusLeave}
          onClick={(e) => handleStatusClick(e, performance)}
        >
          {badgeElement}
        </div>
      );
    }

    return badgeElement;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getNoDataColSpan = () => {
    let span = 3;
    if (showGoalsColumn || showScheduleColumn) span += 2;
    if (showNextReviewColumn) span += 1;
    if (showPerformanceColumn) span += 1;
    if (showStatusColumn) span += 1;
    if (showAcceptanceStatusColumn) span += 1;
    return span;
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const emptyRowsCount = Math.max(0, itemsPerPage - data.length);
  const emptyRows = Array.from({ length: emptyRowsCount }, (_, index) => index);

  return (
    <>
      <div
        className="employee-review-table-container"
        onClick={handleBackgroundClick}
      >
        <table className="employee-review-table">
          <thead>
            <tr>
              <th>Employee ID</th>
              <th>Employee Name</th>
              {(showGoalsColumn || showScheduleColumn) && <th>Email</th>}
              {(showGoalsColumn || showScheduleColumn) && (
                <th>Current Title</th>
              )}
              {showNextReviewColumn && <th>Next Review Date</th>}
              {showPerformanceColumn && <th>Performance %</th>}
              {showStatusColumn && <th>Goal Status</th>}
              {showAcceptanceStatusColumn && <th>Acceptance Status</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={getNoDataColSpan()} className="no-data">
                  <Typography
                    text="No employee reviews found"
                    type="base_regular"
                    color="gray"
                  />
                </td>
              </tr>
            ) : (
              data.map((employee) => (
                <tr key={employee.id} className="table-row">
                  <td>
                    <Typography text={employee.id} type="small_regular" />
                  </td>
                  <td
                    className="clickable-name"
                    onClick={() => onEmployeeClick(employee)}
                  >
                    <Typography
                      text={employee.name}
                      type="small_regular"
                      weight="bold"
                    />
                  </td>
                  {(showGoalsColumn || showScheduleColumn) && (
                    <td>
                      <Typography
                        text={employee.upn || 'N/A'}
                        type="small_regular"
                      />
                    </td>
                  )}
                  {(showGoalsColumn || showScheduleColumn) && (
                    <td>
                      <Typography
                        text={employee.designation || 'N/A'}
                        type="small_regular"
                      />
                    </td>
                  )}
                  {showNextReviewColumn && (
                    <td>
                      <Typography
                        text={formatDate(employee.nextReviewDate || '')}
                        type="small_regular"
                      />
                    </td>
                  )}
                  {showPerformanceColumn && (
                    <td>
                      <div className="percentage-cell">
                        <Typography
                          text={`${employee.percentage}%`}
                          type="small_regular"
                        />
                        <div className="percentage-bar">
                          <div
                            className="percentage-fill"
                            style={{ width: `${employee.percentage}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  )}
                  {showStatusColumn && (
                    <td>
                      {getStatusBadge(employee.status, employee.performance)}
                    </td>
                  )}
                  {showAcceptanceStatusColumn && (
                    <td>
                      {employee.acceptanceStatus && employee.goalDetails ? (
                        getAcceptanceStatusBadge(
                          employee.acceptanceStatus,
                          employee.goalDetails,
                          employee.performance
                        )
                      ) : (
                        <Badge label="N/A" size="sm" type="secondary" />
                      )}
                    </td>
                  )}
                  <td>
                    <button
                      className="action-button"
                      onClick={() => onEmployeeClick(employee)}
                    >
                      <img
                        src={
                          showScheduleColumn
                            ? '/icons/calendar.svg'
                            : '/icons/admin-edit.svg'
                        }
                        alt={showScheduleColumn ? 'Schedule' : 'Edit'}
                      />
                    </button>
                  </td>
                </tr>
              ))
            )}
            {showPagination &&
              emptyRows.map((index) => (
                <tr key={`empty-${index}`} className="empty-row">
                  <td colSpan={getNoDataColSpan()}>&nbsp;</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {showPagination && onPageChange && onItemsPerPageChange && (
        <EmployeeReviewPagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      )}
      {/* Performance Tooltip */}
      {tooltipState.performance && (
        <PerformanceTooltip
          performance={tooltipState.performance}
          isVisible={tooltipState.isVisible}
          position={tooltipState.position}
          showBelow={tooltipState.showBelow}
          isPinned={tooltipState.isPinned}
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleTooltipLeave}
        />
      )}
    </>
  );
};
